type FlexGridBoxesCallback = (selected: HTMLElement) => void | null;
type FlexGridBoxesElementOptions = {
    innerHTML?: Map<string, string>;
    dataValues?: string[];
    attributes?: string[];
    className?: string[];
};

/**
 * Format elements to display as responsive tiles.
 *
 * @property {HTMLCollectionOf<Element>} elements The elements that will be formatted to be tiles.
 * @property {object} element_options An object that holds options for the elements.
 * @property {boolean} create_elements A flag that triggers if HTML elements need to be created.
 * @property {HTMLElement} append_container The container that the HTML elements append to.
 * @property {function|null} callback for the click event on a tile.
 */
export class FlexGridBoxes {
    elements: HTMLCollectionOf<HTMLElement> | HTMLElement[];
    element_options: FlexGridBoxesElementOptions;
    create_elements: boolean;
    append_container: HTMLElement;
    callback: FlexGridBoxesCallback;
    locked: boolean;

    /**
     * Accepts html elements or an object that returns elements formatted as responsive tiles.
     *
     * @param {HTMLCollection} elements The elements that will be formatted to be tiles.
     * @param {object} element_options An object that holds options for the elements.
     * @param {boolean} create_elements A flag that triggers if HTML elements need to be created.
     * @param {HTMLElement} append_container The container that the HTML elements append to.
     * @param {function|null} callback for the click event on a tile. (optional)
     */
    constructor(
        elements: HTMLCollectionOf<HTMLElement> | HTMLElement[],
        element_options: FlexGridBoxesElementOptions = {},
        create_elements: boolean = false,
        append_container: HTMLElement = null,
        callback: FlexGridBoxesCallback = null
    ) {
        this.elements = elements;
        this.element_options = element_options;
        this.create_elements = create_elements;
        this.append_container = append_container;
        this.callback = callback;
        this.locked = false;
    }

    /**
     * Bind the elements to a click event that handles a selected element.
     *
     * @param {Array} elements The array of elements to bind the click event to.
     * @returns {void}
     */
    bind(elements: HTMLCollectionOf<HTMLElement> | HTMLElement[]) : void {
        for (const element of elements) {
            element.addEventListener('click', this.element_clicked.bind(this), false);
        }
    }

    /**
     * Adds a CSS class to the selected element and pass the element to the callback.
     *
     * @param {Event} event The event from the clicked element.
     * @returns {boolean} True if the link is allowed to be clicked, false otherwise.
     */
    element_clicked(event: Event) {
        if (this.locked) {
            event.preventDefault();
            event.stopPropagation();
            return false;
        }

        this.select_element(event.currentTarget as HTMLElement);

        return true;
    }

    /**
     * Selects an element by simply providing the element's index.
     *
     * @param {number} idx to select.
     * @return {void}
     */
    select_by_idx(idx: number) {
        if (idx >= 0 && idx < this.elements.length) {
            this.select_element(this.elements[idx]);
        }
    }

    /**
     * Selects the passed element.
     *
     * @param {HTMLElement} selected element.
     * @return {void}
     */
    select_element(selected: HTMLElement) {
        for (const element of this.elements) {
            element.classList.remove('tv-flex-grid-box__selected');
        }

        selected.classList.add('tv-flex-grid-box__selected');

        if (this.callback) {
            this.callback(selected);
        }
    }

    /**
     * Returns the element that is currently selected.
     *
     * @return {HTMLElement}|null that is currently selected determined by class name. Null if none selected.
     */
    selected_element(): HTMLElement {
        for (const element of this.elements) {
            if (element.classList.contains('tv-flex-grid-box__selected')) {
                return element;
            }
        }

        return null;
    }

    /**
     * Setup the elements by creating them and/or adding the appropriate CSS classes for correct display.
     *
     * @returns {void}
     */
    setup() : void {
        if (this.create_elements) {
            // TODO : Change this to something that can easily convert to an HTMLCollection.
            const html_elements = [];
            for (const element of this.elements) {
                const html_element = document.createElement('div');

                if (this.element_options) {
                    if (this.element_options.innerHTML) {
                        for (const [key, value] of this.element_options.innerHTML) {
                            const span_element = document.createElement('span');
                            span_element.innerHTML = element[key];
                            span_element.classList.add(value);
                            html_element.innerHTML += span_element.outerHTML;
                        }
                    }
                    if (this.element_options.dataValues) {
                        for (const dataValue of this.element_options.dataValues) {
                            html_element.dataset[dataValue] = element[dataValue];
                        }
                    }
                    if (this.element_options.attributes) {
                        for (const attribute of this.element_options.attributes) {
                            html_element.setAttribute(attribute, element[attribute]);
                        }
                    }
                    if (this.element_options.className) {
                        html_element.classList.add(...this.element_options.className);
                    }
                }
                /** @type {HTMLElement} */ (this.append_container).appendChild(html_element);
                html_elements.push(html_element);
            }
            this.elements = html_elements;
        }
        for (const e of this.elements) {
            const element = /** @type {HTMLElement} */ (e);
            if (!element.classList.contains('tv-flex-grid-box')) {
                element.classList.add('tv-flex-grid-box');
            }
        }
        this.bind(this.elements);

        // Default to selecting the first item.
        this.select_by_idx(0);
    }
}